/**
 * Single-row overflow for the editor toolbar.
 *
 * The pure half (`computeToolbarLayout`, `dividersBetween`) decides WHICH
 * items stay in the row for a given container width; the hook half
 * measures the real DOM and drives React state from it. Keeping the
 * arithmetic pure is what makes it unit-testable without a layout engine
 * — jsdom has no `getBoundingClientRect`.
 *
 * Algorithm (measure-based, the Atlassian / Fluent "responsive toolbar"):
 *   1. Everything visible, Tt▾ at full width. Fits? Done.
 *   2. Shrink Tt▾ to its icon-only stage (it is the widest item, ~90px of
 *      label). Fits? Done.
 *   3. Remove the lowest-priority visible item (ties → rightmost) until it
 *      fits. The ⋯ More button's width is reserved as soon as one item is
 *      hidden — or always, when the preset has menu-only items.
 *
 * Dividers are a RENDER concern: one between two adjacent visible items of
 * different groups, never leading / trailing / before ⋯. Their width is
 * still part of the fit arithmetic, so the pure function counts them from
 * the surviving neighbours on every pass.
 *
 * Measurement rules the hook enforces (each one a real failure mode):
 *   - Widths are read once per (item set, locale) and cached; hidden items
 *     are not in the DOM, so a re-render cannot re-measure them.
 *   - Re-measure on `i18n.language` (the Tt label and every tooltip change
 *     length) and after `document.fonts.ready` (a fallback-font first paint
 *     under-measures the serif `Tt`; the state would never self-correct).
 *   - State only changes when the resulting `(compact, visibleIds)` tuple
 *     changes, so a ResizeObserver firing 60×/s during a window drag does
 *     not re-render the row on every tick.
 */

import { useCallback, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

/**
 * The Tt trigger's own `gap-1` between its label and its glyphs — what the
 * compact stage saves on top of the label width. A constant, NOT the row
 * gap: on the wiki row (gap 6) using the row gap under-estimated the
 * compact width by 2px and the row could clip the ⋯ edge.
 */
const COMPACT_LABEL_GAP = 4

export interface LayoutItem {
  id: string
  group: string
  /** Lower collapses first. */
  priority: number
  /** Measured width at the item's full presentation. */
  width: number
  /**
   * Measured width of the compact presentation, when the item has one.
   * Only the text-style dropdown does today.
   */
  compactWidth?: number
}

export interface LayoutMetrics {
  containerWidth: number
  /** The ⋯ trigger's width. */
  moreWidth: number
  /** Flex gap between adjacent children. */
  gap: number
  /** A divider's own width (its margins are part of `gap` arithmetic only if they are flex gaps — ours are margins, so include them here). */
  dividerWidth: number
  /** When true the ⋯ button is always reserved (the preset has menu-only items). */
  alwaysShowMore: boolean
}

export interface ToolbarLayout {
  visibleIds: readonly string[]
  hiddenIds: readonly string[]
  /** True when the compact-capable item should render its compact stage. */
  compact: boolean
  showMore: boolean
}

/** Group boundaries among an ordered list of visible items. */
export const dividersBetween = (items: readonly Pick<LayoutItem, 'group'>[]): number => {
  let count = 0
  for (let i = 1; i < items.length; i += 1) {
    const prev = items[i - 1]
    const cur = items[i]
    if (prev && cur && prev.group !== cur.group) count += 1
  }
  return count
}

const rowWidth = (
  visible: readonly LayoutItem[],
  compact: boolean,
  metrics: LayoutMetrics,
  showMore: boolean,
): number => {
  if (visible.length === 0) return showMore ? metrics.moreWidth : 0
  const items = visible.reduce(
    (sum, item) =>
      sum + (compact && item.compactWidth !== undefined ? item.compactWidth : item.width),
    0,
  )
  const dividers = dividersBetween(visible)
  const children = visible.length + dividers + (showMore ? 1 : 0)
  return (
    items +
    dividers * metrics.dividerWidth +
    (children - 1) * metrics.gap +
    (showMore ? metrics.moreWidth : 0)
  )
}

export const computeToolbarLayout = (
  items: readonly LayoutItem[],
  metrics: LayoutMetrics,
): ToolbarLayout => {
  const hasCompactStage = items.some((item) => item.compactWidth !== undefined)
  let visible = [...items]
  let compact = false

  const fits = (candidate: readonly LayoutItem[], isCompact: boolean): boolean => {
    const hidden = candidate.length < items.length
    const showMore = hidden || metrics.alwaysShowMore
    return rowWidth(candidate, isCompact, metrics, showMore) <= metrics.containerWidth
  }

  if (!fits(visible, compact) && hasCompactStage) compact = true

  while (visible.length > 0 && !fits(visible, compact)) {
    // Lowest priority first; ties → rightmost (highest index).
    let victim = -1
    for (let i = 0; i < visible.length; i += 1) {
      const item = visible[i]
      if (!item) continue
      const current = visible[victim]
      if (victim === -1 || (current && item.priority <= current.priority)) victim = i
    }
    if (victim === -1) break
    visible = visible.filter((_, index) => index !== victim)
  }

  // Hysteresis by construction: compact stays on only while the full
  // presentation would not fit with the SAME visible set.
  if (
    compact &&
    rowWidth(visible, false, metrics, visible.length < items.length || metrics.alwaysShowMore) <=
      metrics.containerWidth
  ) {
    compact = false
  }

  const visibleIds = visible.map((item) => item.id)
  const visibleSet = new Set(visibleIds)
  const hiddenIds = items.filter((item) => !visibleSet.has(item.id)).map((item) => item.id)
  return {
    visibleIds,
    hiddenIds,
    compact,
    showMore: hiddenIds.length > 0 || metrics.alwaysShowMore,
  }
}

/* ------------------------------------------------------------------------ */
/* Hook                                                                     */
/* ------------------------------------------------------------------------ */

export interface OverflowItemSpec {
  id: string
  group: string
  priority: number
  /** The item renders a compact stage (only `text-style` today). */
  hasCompactStage?: boolean
}

export interface UseToolbarOverflowOptions {
  items: readonly OverflowItemSpec[]
  /** Reserve ⋯ even when nothing overflowed. */
  alwaysShowMore: boolean
  gap: number
  dividerWidth: number
  /** Fallback ⋯ width used until the trigger has been measured. */
  moreWidthFallback: number
}

export interface UseToolbarOverflowResult extends ToolbarLayout {
  /** Attach to the row container (the flex parent). */
  containerRef: (node: HTMLElement | null) => void
  /** Attach to each rendered item's OUTER element, keyed by item id. */
  itemRef: (id: string) => (node: HTMLElement | null) => void
  /** Attach to the compact-capable item's LABEL span — its width is what the compact stage saves. */
  compactLabelRef: (node: HTMLElement | null) => void
  /** Attach to the ⋯ trigger. */
  moreRef: (node: HTMLElement | null) => void
  /**
   * True while the hook needs every item in the DOM to read its width.
   * The row renders ALL items (at full presentation) during this pass; the
   * layout effect measures and replaces the state before paint.
   */
  measuring: boolean
}

const layoutKey = (layout: ToolbarLayout): string =>
  `${layout.compact ? 'c' : 'f'}|${layout.visibleIds.join(',')}|${layout.showMore ? 'm' : '-'}`

export function useToolbarOverflow({
  items,
  alwaysShowMore,
  gap,
  dividerWidth,
  moreWidthFallback,
}: UseToolbarOverflowOptions): UseToolbarOverflowResult {
  const { i18n } = useTranslation()
  const itemIds = items.map((item) => item.id).join(',')
  // A change in the item set or the locale invalidates every cached width.
  const measureKey = `${itemIds}|${i18n.language}`

  const widthsRef = useRef<Map<string, number>>(new Map())
  const compactLabelWidthRef = useRef(0)
  const moreWidthRef = useRef(moreWidthFallback)
  const measuredKeyRef = useRef<string | null>(null)
  const measureKeyRef = useRef(measureKey)
  measureKeyRef.current = measureKey
  const lastLayoutKeyRef = useRef<string | null>(null)
  const containerWidthRef = useRef(0)
  const containerElRef = useRef<HTMLElement | null>(null)
  const itemElsRef = useRef<Map<string, HTMLElement>>(new Map())
  const compactLabelElRef = useRef<HTMLElement | null>(null)
  const moreElRef = useRef<HTMLElement | null>(null)
  const fontsReadyRef = useRef(false)

  const allVisible = useMemo<ToolbarLayout>(
    () => ({
      visibleIds: items.map((item) => item.id),
      hiddenIds: [],
      compact: false,
      showMore: alwaysShowMore,
    }),
    [items, alwaysShowMore],
  )
  const [layout, setLayout] = useState<ToolbarLayout>(allVisible)
  // Bumped to force a render (and so a fresh measuring pass) when a
  // measurement attempt found nothing to measure — see the layout effect.
  const [, setMeasureTick] = useState(0)
  const measuring = measuredKeyRef.current !== measureKey

  const recompute = useCallback(() => {
    const containerWidth = containerWidthRef.current
    if (containerWidth <= 0) return
    const layoutItems: LayoutItem[] = []
    for (const spec of items) {
      const width = widthsRef.current.get(spec.id)
      if (width === undefined) return // not measured yet — keep the measuring pass
      layoutItems.push({
        id: spec.id,
        group: spec.group,
        priority: spec.priority,
        width,
        compactWidth: spec.hasCompactStage
          ? Math.max(0, width - compactLabelWidthRef.current - COMPACT_LABEL_GAP)
          : undefined,
      })
    }
    const next = computeToolbarLayout(layoutItems, {
      containerWidth,
      moreWidth: moreWidthRef.current,
      gap,
      dividerWidth,
      alwaysShowMore,
    })
    const key = layoutKey(next)
    if (lastLayoutKeyRef.current === key) return
    lastLayoutKeyRef.current = key
    setLayout(next)
  }, [items, gap, dividerWidth, alwaysShowMore])

  // Measurement pass: every item is in the DOM at full presentation.
  //
  // A pass only COUNTS when it read real geometry. A toolbar that mounts
  // inside a container that has no layout yet (the wiki doc re-entering
  // Edit, the rail composer expanding from its placeholder) reads 0 for
  // the container and every item; caching those zeros made "everything
  // fits" the permanent answer — a clipped full row with no ⋯ until the
  // window was resized. Leave the key unset instead: the row keeps
  // rendering every item, and the ResizeObserver re-arms the pass the
  // moment the container gets a size.
  useLayoutEffect(() => {
    if (!measuring) return
    const containerWidth = containerElRef.current?.getBoundingClientRect().width ?? 0
    if (containerWidth <= 0) return
    const widths = new Map<string, number>()
    for (const spec of items) {
      const el = itemElsRef.current.get(spec.id)
      if (!el) return // an item hasn't mounted yet; next commit will
      const width = el.getBoundingClientRect().width
      if (width <= 0) return
      widths.set(spec.id, width)
    }
    widthsRef.current = widths
    compactLabelWidthRef.current = compactLabelElRef.current?.getBoundingClientRect().width ?? 0
    if (moreElRef.current) moreWidthRef.current = moreElRef.current.getBoundingClientRect().width
    measuredKeyRef.current = measureKey
    containerWidthRef.current = containerWidth
    recompute()
  })

  // Fonts: a fallback-font first paint under-measures; re-measure once.
  useLayoutEffect(() => {
    if (fontsReadyRef.current || typeof document === 'undefined' || !('fonts' in document)) return
    let cancelled = false
    document.fonts.ready.then(() => {
      if (cancelled) return
      fontsReadyRef.current = true
      measuredKeyRef.current = null
      lastLayoutKeyRef.current = null
      // Force a measuring pass by resetting to all-visible.
      setLayout(allVisible)
      setMeasureTick((tick) => tick + 1)
    })
    return () => {
      cancelled = true
    }
  }, [allVisible])

  const observerRef = useRef<ResizeObserver | null>(null)
  const containerRef = useCallback(
    (node: HTMLElement | null) => {
      observerRef.current?.disconnect()
      observerRef.current = null
      containerElRef.current = node
      if (!node || typeof ResizeObserver === 'undefined') return
      const observer = new ResizeObserver((entries) => {
        const entry = entries[0]
        if (!entry) return
        containerWidthRef.current = entry.contentRect.width
        if (measuredKeyRef.current !== measureKeyRef.current) {
          // Nothing measured yet (see the layout effect) — a render
          // re-runs the pass now that the container has a size.
          if (entry.contentRect.width > 0) setMeasureTick((tick) => tick + 1)
          return
        }
        recompute()
      })
      observer.observe(node)
      observerRef.current = observer
      containerWidthRef.current = node.getBoundingClientRect().width
    },
    [recompute],
  )

  const itemRef = useCallback(
    (id: string) => (node: HTMLElement | null) => {
      if (node) itemElsRef.current.set(id, node)
      else itemElsRef.current.delete(id)
    },
    [],
  )
  const compactLabelRef = useCallback((node: HTMLElement | null) => {
    compactLabelElRef.current = node
  }, [])
  const moreRef = useCallback((node: HTMLElement | null) => {
    moreElRef.current = node
  }, [])

  // While measuring, present everything so the DOM carries every item.
  const effective = measuring ? allVisible : layout

  return {
    ...effective,
    containerRef,
    itemRef,
    compactLabelRef,
    moreRef,
    measuring,
  }
}
